/**
 * Fork-specific: resolves which image (SOP instance / frame) the favorite star
 * should act on.
 *
 * - If the display set is shown in a viewport, use the slice currently visible
 *   in that viewport (per the "corte atualmente visível" product decision).
 * - Otherwise fall back to the first image of the series.
 */

export type FavoriteInstance = {
  studyInstanceUID: string;
  seriesInstanceUID: string;
  sopInstanceUID: string;
  frameNumber: number;
  seriesDescription?: string;
  modality?: string;
  instanceNumber?: number;
};

function getCurrentImageIndex(csViewport: any): number | undefined {
  try {
    if (csViewport && typeof csViewport.getCurrentImageIdIndex === 'function') {
      const index = csViewport.getCurrentImageIdIndex();
      if (Number.isInteger(index) && index >= 0) {
        return index;
      }
    }
  } catch (error) {
    // volume viewports / not-yet-rendered viewports can throw; fall back
  }
  return undefined;
}

export function resolveFavoriteInstance({
  displaySet,
  cornerstoneViewport,
}: {
  displaySet: any;
  cornerstoneViewport?: any;
}): FavoriteInstance | null {
  const images = displaySet?.images;
  if (!displaySet || !Array.isArray(images) || images.length === 0) {
    return null;
  }

  const isMultiFrame = images.length === 1 && (displaySet.numImageFrames ?? 1) > 1;
  const currentIndex = getCurrentImageIndex(cornerstoneViewport) ?? 0;

  let image;
  let frameNumber = 1;

  if (isMultiFrame) {
    image = images[0];
    frameNumber = currentIndex + 1;
  } else {
    image = images[Math.min(currentIndex, images.length - 1)] ?? images[0];
  }

  if (!image?.SOPInstanceUID) {
    return null;
  }

  return {
    studyInstanceUID: displaySet.StudyInstanceUID,
    seriesInstanceUID: displaySet.SeriesInstanceUID,
    sopInstanceUID: image.SOPInstanceUID,
    frameNumber,
    seriesDescription: displaySet.SeriesDescription,
    modality: displaySet.Modality,
    instanceNumber: image.InstanceNumber ? Number(image.InstanceNumber) : undefined,
  };
}
